import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, Download, Eye, FileText, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { downloadOfferPdf, previewOfferPdf } from "@/lib/generateOfferPdf";

const DEFAULT_RATES_BLEND = [
  { method: "Visa / Mastercard (personnelles CH/UE)", rate: "0.85", fixed: "0.05", notes: "Toutes cartes conso" },
  { method: "Visa / Mastercard (business/premium)", rate: "1.60", fixed: "0.05", notes: "Cartes corporate/commercial" },
  { method: "American Express", rate: "2.50", fixed: "0.10", notes: "" },
  { method: "Twint", rate: "1.30", fixed: "0.00", notes: "" },
  { method: "Apple Pay / Google Pay", rate: "0.85", fixed: "0.05", notes: "Même barème que la carte utilisée" },
];

// IC++ pricing model — same table structure as Blend (method, rate, fixed, notes).
// The rate shown here is the effective total (Interchange + Scheme + Margin combined).
const DEFAULT_RATES_ICPP = [
  { method: "Visa / Mastercard (personnelles CH/UE)", rate: "0.60", fixed: "0.05", notes: "IC + Scheme + Marge" },
  { method: "Visa / Mastercard (business/premium)", rate: "1.95", fixed: "0.05", notes: "IC business + Scheme + Marge" },
  { method: "Visa / Mastercard (hors UE)", rate: "1.65", fixed: "0.05", notes: "IC international + Scheme + Marge" },
  { method: "American Express", rate: "2.50", fixed: "0.10", notes: "" },
  { method: "Twint", rate: "1.30", fixed: "0.00", notes: "" },
  { method: "Apple Pay / Google Pay", rate: "0.60", fixed: "0.05", notes: "Même barème que la carte utilisée" },
];

const RATE_MODELS = [
  { id: "blend", label: "Full Blend", description: "Un taux unique par moyen de paiement. Facturation simple et prévisible." },
  { id: "icpp", label: "Interchange++ (IC++)", description: "Facturation transparente : Interchange + Scheme + Marge acquéreur. Idéal pour gros volumes." },
];

const DEFAULT_EQUIPMENT = [
  { model: "AMS1", description: "Terminal mobile · Écran 5.5\" · 4G/5G · Twint", qty: 1, price: 0 },
];

const TERMINAL_OPTIONS = [
  { model: "AMS1", description: "Terminal mobile · 5.5\" · 4G/5G · Twint" },
  { model: "NYC1", description: "Terminal mobile · 6\" · 4G/5G · Imprimante" },
  { model: "SFO1", description: "Terminal fixe · 8\" · Ethernet + WiFi" },
  { model: "S1F2", description: "POS intégré · 15.6\" · Android · Imprimante" },
  { model: "P630", description: "Terminal MPOS · Bluetooth · Compact" },
  { model: "M450", description: "Terminal fixe · Imprimante · Ethernet" },
  { model: "S1E2L", description: "Terminal Android · Écran 5\" · 4G" },
];

function generateOfferNumber() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const rand = String(Math.floor(Math.random() * 900) + 100);
  return `AYA-${y}${m}${day}-${rand}`;
}

function today() {
  return new Date().toISOString().slice(0, 10);
}
function daysFromNow(n) {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}
function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("fr-CH", { day: "2-digit", month: "2-digit", year: "numeric" });
}

export default function OffersTab({ settings = {} }) {
  const [offer, setOffer] = useState({
    offerNumber: generateOfferNumber(),
    issueDateISO: today(),
    validUntilISO: daysFromNow(30),
    intro: "",
    salesRep: "Emrah AYAZ",
    notes: "",
    rateModel: "blend", // "blend" or "icpp"
    client: {
      company: "",
      contact: "",
      address: "",
      postalCode: "",
      city: "",
      country: "Suisse",
      email: "",
      phone: "",
      ide: "",
    },
    rates: DEFAULT_RATES_BLEND,
    equipment: DEFAULT_EQUIPMENT,
    conditions: [
      "Engagement contractuel : 1 an, avec 1 mois d'essai gratuit.",
      "Frais d'activation unique : CHF 199.- (paramétrage compte marchand, configuration terminal, formation initiale, activation support).",
      "Délai de versement des fonds : J+2 (jours ouvrés) sur votre compte bancaire suisse.",
      "Terminal, assurance, maintenance, SIM 5G et support 24/7 inclus.",
      "Conformité PCI-DSS Niveau 1 · FINMA · LPD / RGPD.",
      "Aucun frais caché · Résiliation possible à l'échéance sans engagement.",
    ],
  });

  const pdfPayload = useMemo(() => ({
    ...offer,
    issueDate: fmtDate(offer.issueDateISO),
    validUntil: fmtDate(offer.validUntilISO),
    company: {
      name: settings.company_name || "AyaPay",
      email: settings.email || "contact@ayapay.ch",
      phone: settings.phone || "+41 76 200 36 12",
      address: settings.address_street || settings.address || "Chemin des Lilas 12",
      postalCity: settings.address_city || "2503 Biel/Bienne",
    },
  }), [offer, settings]);

  const setClient = (key, value) => setOffer((p) => ({ ...p, client: { ...p.client, [key]: value } }));
  const setField = (key, value) => setOffer((p) => ({ ...p, [key]: value }));

  const addRate = () => setOffer((p) => ({ ...p, rates: [...p.rates, { method: "", rate: "", fixed: "", notes: "" }] }));
  const updateRate = (i, key, value) => setOffer((p) => ({ ...p, rates: p.rates.map((r, idx) => idx === i ? { ...r, [key]: value } : r) }));
  const removeRate = (i) => setOffer((p) => ({ ...p, rates: p.rates.filter((_, idx) => idx !== i) }));

  const switchRateModel = (newModel) => {
    if (newModel === offer.rateModel) return;
    if (!window.confirm(`Basculer vers le modèle ${newModel === "icpp" ? "IC++" : "Full Blend"} ? La grille tarifaire actuelle sera remplacée par les valeurs par défaut de ce modèle.`)) return;
    setOffer((p) => ({
      ...p,
      rateModel: newModel,
      rates: newModel === "icpp" ? DEFAULT_RATES_ICPP : DEFAULT_RATES_BLEND,
    }));
  };

  const addEquipment = (preset) => {
    const item = preset
      ? { model: preset.model, description: preset.description, qty: 1, price: 0 }
      : { model: "", description: "", qty: 1, price: 0 };
    setOffer((p) => ({ ...p, equipment: [...p.equipment, item] }));
  };
  const updateEquipment = (i, key, value) => setOffer((p) => ({ ...p, equipment: p.equipment.map((e, idx) => idx === i ? { ...e, [key]: value } : e) }));
  const removeEquipment = (i) => setOffer((p) => ({ ...p, equipment: p.equipment.filter((_, idx) => idx !== i) }));

  const updateCondition = (i, value) => setOffer((p) => ({ ...p, conditions: p.conditions.map((c, idx) => idx === i ? value : c) }));
  const addCondition = () => setOffer((p) => ({ ...p, conditions: [...p.conditions, ""] }));
  const removeCondition = (i) => setOffer((p) => ({ ...p, conditions: p.conditions.filter((_, idx) => idx !== i) }));

  const handleDownload = async () => {
    if (!offer.client.company) { toast.error("Renseignez au minimum le nom du client"); return; }
    try {
      await downloadOfferPdf(pdfPayload);
      toast.success("Offre PDF générée");
    } catch (e) {
      toast.error("Erreur génération PDF");
      console.error(e);
    }
  };
  const handlePreview = async () => {
    if (!offer.client.company) { toast.error("Renseignez au minimum le nom du client"); return; }
    try { await previewOfferPdf(pdfPayload); }
    catch (e) { toast.error("Erreur aperçu"); console.error(e); }
  };
  const handleReset = () => {
    setOffer((p) => ({ ...p, offerNumber: generateOfferNumber(), client: { company: "", contact: "", address: "", postalCode: "", city: "", country: "Suisse", email: "", phone: "", ide: "" } }));
    toast.success("Nouveau numéro d'offre généré");
  };

  return (
    <div data-testid="offers-tab">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#0A1128]">Générateur d'offres PDF</h1>
          <p className="text-sm text-[#475569] mt-1">Créez une offre commerciale premium avec votre logo, votre grille tarifaire et vos conditions.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={handleReset} variant="outline" data-testid="offer-reset-btn" className="rounded-lg">
            <Plus size={16} className="mr-1.5" /> Nouvelle offre
          </Button>
          <Button onClick={handlePreview} variant="outline" data-testid="offer-preview-btn" className="rounded-lg">
            <Eye size={16} className="mr-1.5" /> Aperçu PDF
          </Button>
          <Button onClick={handleDownload} data-testid="offer-download-btn" className="bg-[#00C853] hover:bg-[#00A844] text-white rounded-lg">
            <Download size={16} className="mr-1.5" /> Télécharger PDF
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* LEFT — Form */}
        <div className="xl:col-span-2 space-y-6">
          {/* Offer meta */}
          <Section title="Informations sur l'offre" icon={FileText}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="N° d'offre">
                <Input data-testid="offer-number" value={offer.offerNumber} onChange={(e) => setField("offerNumber", e.target.value)} />
              </Field>
              <Field label="Date d'émission">
                <Input type="date" value={offer.issueDateISO} onChange={(e) => setField("issueDateISO", e.target.value)} />
              </Field>
              <Field label="Valable jusqu'au">
                <Input type="date" value={offer.validUntilISO} onChange={(e) => setField("validUntilISO", e.target.value)} />
              </Field>
              <Field label="Commercial (signataire)" className="md:col-span-2">
                <Input value={offer.salesRep} onChange={(e) => setField("salesRep", e.target.value)} placeholder="Nom du commercial" />
              </Field>
            </div>
            <Field label="Texte d'introduction (optionnel)" className="mt-3">
              <Textarea rows={3} value={offer.intro} onChange={(e) => setField("intro", e.target.value)} placeholder="Message d'accompagnement de l'offre. Laissez vide pour utiliser le texte par défaut." className="resize-none" />
            </Field>
          </Section>

          {/* Client */}
          <Section title="Coordonnées du client" icon={ChevronRight}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Raison sociale *"><Input data-testid="client-company" value={offer.client.company} onChange={(e) => setClient("company", e.target.value)} placeholder="Restaurant XYZ SA" /></Field>
              <Field label="Personne de contact"><Input value={offer.client.contact} onChange={(e) => setClient("contact", e.target.value)} placeholder="M. Dupont" /></Field>
              <Field label="Adresse" className="md:col-span-2"><Input value={offer.client.address} onChange={(e) => setClient("address", e.target.value)} placeholder="Rue du Commerce 12" /></Field>
              <Field label="Code postal"><Input value={offer.client.postalCode} onChange={(e) => setClient("postalCode", e.target.value)} placeholder="1201" /></Field>
              <Field label="Ville"><Input value={offer.client.city} onChange={(e) => setClient("city", e.target.value)} placeholder="Genève" /></Field>
              <Field label="Pays"><Input value={offer.client.country} onChange={(e) => setClient("country", e.target.value)} /></Field>
              <Field label="IDE (optionnel)"><Input value={offer.client.ide} onChange={(e) => setClient("ide", e.target.value)} placeholder="CHE-123.456.789" /></Field>
              <Field label="Email"><Input type="email" value={offer.client.email} onChange={(e) => setClient("email", e.target.value)} placeholder="contact@client.ch" /></Field>
              <Field label="Téléphone"><Input value={offer.client.phone} onChange={(e) => setClient("phone", e.target.value)} placeholder="+41 22 ..." /></Field>
            </div>
          </Section>

          {/* Rates */}
          <Section title="Grille tarifaire personnalisée" icon={ChevronRight} action={
            <Button size="sm" onClick={addRate} data-testid="add-rate-btn" className="bg-[#1B2B5B] hover:bg-[#0f1d3d] text-white rounded-md text-xs h-8">
              <Plus size={14} className="mr-1" /> Ligne
            </Button>
          }>
            {/* Rate model selector — Full Blend vs IC++ */}
            <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-3">
              {RATE_MODELS.map((rm) => (
                <button
                  key={rm.id}
                  onClick={() => switchRateModel(rm.id)}
                  data-testid={`rate-model-${rm.id}`}
                  className={`text-left p-4 rounded-xl border-2 transition-all ${
                    offer.rateModel === rm.id
                      ? "border-[#00C853] bg-[#00C853]/5 shadow-sm"
                      : "border-slate-200 hover:border-slate-300 bg-white"
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${offer.rateModel === rm.id ? "border-[#00C853]" : "border-slate-300"}`}>
                      {offer.rateModel === rm.id && <div className="w-2 h-2 rounded-full bg-[#00C853]" />}
                    </div>
                    <span className="text-sm font-semibold text-[#0A1128]">{rm.label}</span>
                  </div>
                  <p className="text-xs text-slate-500 leading-relaxed pl-6">{rm.description}</p>
                </button>
              ))}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-slate-500 uppercase tracking-wide">
                    <th className="pb-2 pr-2 font-medium">Moyen de paiement</th>
                    <th className="pb-2 pr-2 font-medium w-24">Taux (%)</th>
                    <th className="pb-2 pr-2 font-medium w-28">Frais fixes (CHF)</th>
                    <th className="pb-2 pr-2 font-medium">Notes</th>
                    <th className="pb-2 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {offer.rates.map((r, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="py-2 pr-2"><Input value={r.method || ""} onChange={(e) => updateRate(i, "method", e.target.value)} className="h-9 text-sm" /></td>
                      <td className="py-2 pr-2"><Input value={r.rate || ""} onChange={(e) => updateRate(i, "rate", e.target.value)} className="h-9 text-sm" placeholder="0.85" /></td>
                      <td className="py-2 pr-2"><Input value={r.fixed || ""} onChange={(e) => updateRate(i, "fixed", e.target.value)} className="h-9 text-sm" placeholder="0.05" /></td>
                      <td className="py-2 pr-2"><Input value={r.notes || ""} onChange={(e) => updateRate(i, "notes", e.target.value)} className="h-9 text-sm" /></td>
                      <td className="py-2"><button onClick={() => removeRate(i)} className="p-1.5 text-red-400 hover:text-red-600 rounded hover:bg-red-50"><Trash2 size={14} /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          {/* Equipment */}
          <Section title="Équipement mis à disposition" icon={ChevronRight} action={
            <div className="flex gap-2 flex-wrap">
              <select onChange={(e) => { if (e.target.value) { addEquipment(TERMINAL_OPTIONS.find((t) => t.model === e.target.value)); e.target.value = ""; } }} className="text-xs h-8 rounded-md border border-slate-200 px-2 bg-white">
                <option value="">+ Ajouter un modèle</option>
                {TERMINAL_OPTIONS.map((t) => <option key={t.model} value={t.model}>{t.model}</option>)}
              </select>
              <Button size="sm" onClick={() => addEquipment()} className="bg-[#1B2B5B] hover:bg-[#0f1d3d] text-white rounded-md text-xs h-8">
                <Plus size={14} className="mr-1" /> Ligne libre
              </Button>
            </div>
          }>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-slate-500 uppercase tracking-wide">
                    <th className="pb-2 pr-2 font-medium w-24">Modèle</th>
                    <th className="pb-2 pr-2 font-medium">Description</th>
                    <th className="pb-2 pr-2 font-medium w-16">Qté</th>
                    <th className="pb-2 pr-2 font-medium w-24">Prix (CHF)</th>
                    <th className="pb-2 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {offer.equipment.map((e, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="py-2 pr-2"><Input value={e.model} onChange={(ev) => updateEquipment(i, "model", ev.target.value)} className="h-9 text-sm" /></td>
                      <td className="py-2 pr-2"><Input value={e.description} onChange={(ev) => updateEquipment(i, "description", ev.target.value)} className="h-9 text-sm" /></td>
                      <td className="py-2 pr-2"><Input type="number" min="1" value={e.qty} onChange={(ev) => updateEquipment(i, "qty", ev.target.value)} className="h-9 text-sm" /></td>
                      <td className="py-2 pr-2"><Input value={e.price} onChange={(ev) => updateEquipment(i, "price", ev.target.value)} className="h-9 text-sm" placeholder="0 = gratuit" /></td>
                      <td className="py-2"><button onClick={() => removeEquipment(i)} className="p-1.5 text-red-400 hover:text-red-600 rounded hover:bg-red-50"><Trash2 size={14} /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          {/* Conditions */}
          <Section title="Conditions de l'offre" icon={ChevronRight} action={
            <Button size="sm" onClick={addCondition} className="bg-[#1B2B5B] hover:bg-[#0f1d3d] text-white rounded-md text-xs h-8">
              <Plus size={14} className="mr-1" /> Condition
            </Button>
          }>
            <div className="space-y-2">
              {offer.conditions.map((c, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className="text-[#00C853] font-bold mt-2">•</span>
                  <Textarea rows={1} value={c} onChange={(e) => updateCondition(i, e.target.value)} className="flex-1 text-sm min-h-[38px] resize-none" />
                  <button onClick={() => removeCondition(i)} className="p-1.5 text-red-400 hover:text-red-600 rounded hover:bg-red-50 mt-1"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          </Section>

          {/* Notes */}
          <Section title="Notes complémentaires (optionnel)" icon={ChevronRight}>
            <Textarea rows={3} value={offer.notes} onChange={(e) => setField("notes", e.target.value)} placeholder="Toute information supplémentaire à faire figurer sur l'offre..." className="resize-none" />
          </Section>
        </div>

        {/* RIGHT — Live summary preview */}
        <div className="xl:col-span-1">
          <div className="sticky top-8 bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
            <div className="bg-gradient-to-br from-[#1B2B5B] to-[#0F1B3F] p-5 text-white">
              <div className="flex items-baseline justify-between">
                <span className="text-xs font-bold tracking-wider uppercase text-[#00C853]">Aperçu offre</span>
                <span className="text-xs text-slate-300">{offer.offerNumber}</span>
              </div>
              <h3 className="text-xl font-bold mt-2 truncate" style={{ fontFamily: "Outfit, sans-serif" }}>
                {offer.client.company || "Nom du client"}
              </h3>
              <p className="text-xs text-slate-300 mt-1">
                Émise le {fmtDate(offer.issueDateISO)} · Valable jusqu'au {fmtDate(offer.validUntilISO)}
              </p>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <div className="text-xs text-slate-500 uppercase tracking-wide mb-1">Contact</div>
                <div className="text-sm text-slate-900">{offer.client.contact || "-"}</div>
                <div className="text-xs text-slate-500">{offer.client.email || ""}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500 uppercase tracking-wide mb-1">Modèle tarifaire</div>
                <div className="text-sm text-slate-900 font-medium">{offer.rateModel === "icpp" ? "Interchange++ (IC++)" : "Full Blend"}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500 uppercase tracking-wide mb-1">Grille tarifaire</div>
                <div className="text-sm text-slate-900">{offer.rates.length} ligne(s)</div>
              </div>
              <div>
                <div className="text-xs text-slate-500 uppercase tracking-wide mb-1">Équipement</div>
                <div className="text-sm text-slate-900">
                  {offer.equipment.length > 0
                    ? offer.equipment.map((e) => `${e.qty || 1}× ${e.model || "?"}`).join(", ")
                    : "Aucun"}
                </div>
              </div>
              <div className="pt-3 border-t border-slate-100">
                <Button onClick={handleDownload} className="w-full bg-[#00C853] hover:bg-[#00A844] text-white rounded-lg">
                  <Download size={16} className="mr-1.5" /> Télécharger le PDF
                </Button>
                <Button onClick={handlePreview} variant="outline" className="w-full mt-2 rounded-lg">
                  <Eye size={16} className="mr-1.5" /> Aperçu dans un nouvel onglet
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, action, children }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
      <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
        <div className="flex items-center gap-2">
          {Icon && <Icon size={16} className="text-[#00C853]" />}
          <h3 className="text-sm font-semibold text-[#0A1128]">{title}</h3>
        </div>
        {action}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function Field({ label, className = "", children }) {
  return (
    <div className={className}>
      <label className="block text-xs font-medium text-slate-500 mb-1.5">{label}</label>
      {children}
    </div>
  );
}
